import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { Close, SupervisedUserCircle } from '@mui/icons-material';
import React, { useState, useCallback, useEffect } from 'react';
import { getAllFixedSelections } from 'src/shared/services/groupMessageService';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Member } from 'src/modules/member/models/member';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMembers } from 'src/shared/services/memberService';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { MemberSearchFilter } from 'src/modules/member/models/memberSearchFilter';
import { useTranslation } from 'react-i18next';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import SelectionAutocomplete from './selectionAutocomplete';
import { FixedSelection } from '../models/fixedSelection';

type FixedSelectionListProps = {
  smsMode: boolean;
  callback: (item: FixedSelection | null) => void;
};

export const FixedSelectionList = (props: FixedSelectionListProps) => {
  const { smsMode, callback } = props;
  const { t } = useTranslation(['member']);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'email', sort: 'asc' }]);

  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>();

  const [fixedSelectionArray, setFixedSelectionArray] = useState<FixedSelection[]>([]);
  const [rows, setRows] = useState<Member[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: 10,
    page: 0,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const isMounted = useIsMounted();

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllFixedSelections(0, 500, 'selection_id', 'desc');
      if (isMounted()) {
        setFixedSelectionArray(response.data.results);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchMemberData = async () => {
      setLoading(true);
      updateData('loading', true);
      try {
        const result = await getMembers(
          data.page,
          data.pageSize,
          sortModel[0].field,
          sortModel[0].sort,
          { groupName: fixedSelection?.value } as MemberSearchFilter,
          [],
          '',
        );
        if (isMounted()) {
          const memberResult = extendObjectWithId(result.data.results, 'member_id');
          updateData('totalRows', result.data.totalItems);
          setRows(memberResult);
        }
      } catch (error) {
        if (isMounted()) {
          ConsoleHelper.log(error);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
          updateData('loading', false);
        }
      }
    };
    if (fixedSelection?.value) fetchMemberData();
  }, [data.page, data.pageSize, fixedSelection, isMounted, sortModel]);

  const onChange = useCallback(
    (item: FixedSelection | null) => {
      callback(item);
      setFixedSelection(item);
    },
    [callback],
  );

  const columns: GridColDef[] = smsMode
    ? [
        { field: 'memberNr', headerName: t('member:tableHeaders.member_id'), flex: 0.5 },
        {
          field: 'lastName',
          headerName: t('member:tableHeaders.name'),
          sortable: false,
          flex: 1,
          minWidth: 150,
          renderCell: (params: GridCellParams) => {
            return (
              <>
                {params.row.firstName || ''} {params.row.lastName || ''}
              </>
            );
          },
        },
        {
          field: 'phone',
          headerName: t('member:tableHeaders.phone'),
          sortable: false,
          flex: 1,
          renderCell: (params: GridCellParams) => {
            const { phone, phoneOther } = params.row;
            return <>{`${[phone, phoneOther].filter((x) => x !== '').join(', ')}`}</>;
          },
        },
        {
          field: 'email',
          headerName: t('member:tableHeaders.email'),
          sortable: false,
          flex: 1,
          minWidth: 150,
          renderCell: (params: GridCellParams) => {
            return <>{params.row.email || ''}</>;
          },
        },
      ]
    : [
        {
          field: 'email',
          headerName: t('member:tableHeaders.email'),
          sortable: false,
          flex: 1,
          minWidth: 150,
          renderCell: (params: GridCellParams) => {
            return <>{params.row.email || ''}</>;
          },
        },
      ];

  return (
    <>
      {loading && <CircularProgress />}
      {!loading && fixedSelectionArray && !fixedSelection && (
        <SelectionAutocomplete<FixedSelection>
          items={fixedSelectionArray}
          callback={(item: FixedSelection | null) => onChange(item)}
          label='formFields.selection'
          nameProp='name'
          valueProp='value'
        />
      )}
      {fixedSelection && (
        <>
          <Typography
            variant='h6'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              borderColor: 'divider',
              borderWidth: 1,
              borderStyle: 'solid',
              borderRadius: 2,
              px: 2,
              py: 1,
              mb: 2,
            }}
            gutterBottom
          >
            <SupervisedUserCircle sx={{ mr: 1 }} />
            {fixedSelection.name}
            {!loading && ` (${data.totalRows})`}
            <IconButton sx={{ ml: 1 }} onClick={() => onChange(null)}>
              <Close />
            </IconButton>
          </Typography>
          <Box sx={{ height: 350, overflowY: 'auto', p: 1, m: -1 }}>
            <DataGrid<Member>
              autoHeight
              columns={columns}
              rows={rows}
              dataModel={data}
              disableSelectionOnClick
              onSelectionModelChange={(newSelectionModel) => {
                setSelectionModel(newSelectionModel);
              }}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                if (newSortModel.length) {
                  setSortModel(newSortModel);
                } else {
                  setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
                }
              }}
              checkboxSelection={false}
              selectionModel={selectionModel}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </Box>
        </>
      )}
    </>
  );
};
